/**
 * Intercom Live Chat Integration
 * Loads and configures the Intercom messenger for customer support
 */
import config from '@/config';

import { logger } from '@/utils/logger';

declare global {
  interface Window {
    Intercom?: (command: string, ...args: unknown[]) => void;
    intercomSettings?: {
      app_id?: string;
      [key: string]: unknown;
    };
  }
}

/**
 * Initialize Intercom messenger
 * Must be called after user authentication
 */
export const initIntercom = (userEmail: string, userName: string) => {
  // Only run in browser environment
  if (typeof window === 'undefined') return;

  const { INTERCOM_APP_ID } = config;
  if (!INTERCOM_APP_ID) {
    logger.warn('Intercom App ID not configured');
    return;
  }

  // Load Intercom widget script if not already loaded
  if (!window.Intercom) {
    (function () {
      const w = window;
      const ic = w.Intercom;
      if (typeof ic === 'function') {
        ic('reattach_activator');
        ic('update', w.intercomSettings);
      } else {
        const d = document;
        const i = function (...args: unknown[]) {
          (i as { c?: (args: unknown[]) => void }).c?.(args);
        };
        (i as { q?: unknown[]; c?: (args: unknown[]) => void }).q = [];
        (i as { c?: (args: unknown[]) => void }).c = function (args: unknown[]) {
          ((i as { q?: unknown[] }).q as unknown[]).push(args);
        };
        w.Intercom = i as (command: string, ...args: unknown[]) => void;
        function l() {
          const s = d.createElement('script');
          s.type = 'text/javascript';
          s.async = true;
          s.src = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`;
          const x = d.getElementsByTagName('script')[0];
          x.parentNode?.insertBefore(s, x);
        }
        if (d.readyState === 'complete') {
          l();
        } else if (
          (w as Window & { attachEvent?: (event: string, listener: () => void) => void })
            .attachEvent
        ) {
          const windowWithAttachEvent = w as unknown as {
            attachEvent: (event: string, listener: () => void) => void;
          };
          windowWithAttachEvent.attachEvent('onload', l);
        } else {
          w.addEventListener('load', l, false);
        }
      }
    })();
  }

  // Boot Intercom with user data
  if (window.Intercom) {
    window.Intercom('boot', {
      app_id: INTERCOM_APP_ID,
      name: userName,
      email: userEmail,
      created_at: new Date().getTime(),
    });
  }
};

/**
 * Open Intercom messenger
 */
export const openIntercom = () => {
  if (window.Intercom) {
    window.Intercom('show');
  }
};

/**
 * Track a custom event in Intercom (e.g., quick action clicks).
 * Mirrors legacy dataProtect: window.Intercom("trackEvent", event)
 */
export const trackIntercomEvent = (eventName: string) => {
  if (window.Intercom) {
    window.Intercom('trackEvent', eventName);
  }
};

/**
 * Update the current Intercom user's attributes (e.g., role after onboarding).
 * Mirrors legacy dataProtect: window.Intercom('update', { role })
 */
export const updateIntercomUser = (attributes: Record<string, unknown>) => {
  if (window.Intercom) {
    window.Intercom('update', attributes);
  }
};

/**
 * Shutdown Intercom (e.g., on logout)
 */
export const shutdownIntercom = () => {
  if (window.Intercom) {
    window.Intercom('shutdown');
  }
};
